import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Grid, Card, CardContent, Chip, LinearProgress, Fade, Grow, IconButton,
    Tabs, Tab, Alert
} from '@mui/material';
import {
    Close as CloseIcon, EmojiEvents as TrophyIcon, Star as StarIcon,
    Lock as LockIcon, Share as ShareIcon
} from '@mui/icons-material';
import Badge, { BADGE_DEFINITIONS } from '../models/Badge';
import ShareDialog from './ShareDialog';

const BadgeCard = ({ badge, isNew = false, showProgress = false, progress = 0 }) => {
    const [showDetails, setShowDetails] = useState(false);

    return (
        <Grow in={true} timeout={300}>
            <Card
                sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    opacity: badge.isUnlocked() ? 1 : 0.6,
                    transform: showDetails ? 'scale(1.02)' : 'scale(1)',
                    border: isNew ? '2px solid' : '1px solid',
                    borderColor: isNew ? 'warning.main' : badge.isUnlocked() ? badge.getRarityColor() : 'divider',
                    background: badge.isUnlocked() 
                        ? `linear-gradient(135deg, ${badge.getRarityColor()}15 0%, ${badge.getTypeColor()}10 100%)`
                        : 'grey.50',
                    '&:hover': {
                        boxShadow: badge.isUnlocked() ? 6 : 2,
                        transform: 'scale(1.02)'
                    },
                    minHeight: 120
                }}
                onClick={() => setShowDetails(!showDetails)}
            >
                {isNew && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            zIndex: 1,
                            background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
                            color: 'white',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            animation: 'pulse 2s infinite'
                        }}
                    >
                        ✨
                    </Box>
                )}

                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    {/* Badge Icon */}
                    <Box sx={{ fontSize: '2rem', mb: 1, filter: badge.isUnlocked() ? 'none' : 'grayscale(100%)' }}>
                        {badge.isUnlocked() ? badge.icon : <LockIcon sx={{ fontSize: '2rem', color: 'text.disabled' }} />}
                    </Box>

                    {/* Badge Name */}
                    <Typography 
                        variant="subtitle2" 
                        sx={{ 
                            fontWeight: 600, 
                            mb: 0.5,
                            color: badge.isUnlocked() ? 'text.primary' : 'text.disabled'
                        }}
                    >
                        {badge.name}
                    </Typography>

                    {/* Rarity Chip */}
                    <Chip
                        label={badge.rarity}
                        size="small"
                        sx={{
                            backgroundColor: badge.getRarityColor(),
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                            mb: 1
                        }}
                    />

                    {/* Progress Bar for locked badges */}
                    {!badge.isUnlocked() && showProgress && (
                        <Box sx={{ mt: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={Math.min(progress * 100, 100)}
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: 'grey.300',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: badge.getTypeColor()
                                    }
                                }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {Math.round(progress * 100)}% Complete
                            </Typography>
                        </Box>
                    )}

                    {/* Expanded Details */}
                    <Fade in={showDetails}>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {badge.description}
                            </Typography>
                            {badge.isUnlocked() && badge.unlockedAt && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                    Unlocked {new Date(badge.unlockedAt).toLocaleDateString()}
                                </Typography>
                            )}
                        </Box>
                    </Fade>
                </CardContent>
            </Card>
        </Grow>
    );
};

const BadgeSystem = ({ open, onClose, statistics, newBadges = [] }) => {
    const [tabValue, setTabValue] = useState(0);
    const [badges, setBadges] = useState([]);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareType, setShareType] = useState('achievement');

    useEffect(() => {
        // Initialize badges with unlock status
        const updatedBadges = BADGE_DEFINITIONS.map(badge => {
            // Create a new Badge instance with all properties
            const badgeInstance = new Badge(
                badge.id,
                badge.name,
                badge.description,
                badge.icon,
                badge.type,
                badge.requirement,
                badge.rarity
            );
            
            // Check if badge should be unlocked based on statistics
            if (shouldUnlockBadge(badge, statistics)) {
                badgeInstance.unlock();
            }
            
            return badgeInstance;
        });

        setBadges(updatedBadges);
    }, [statistics]);

    const shouldUnlockBadge = (badge, stats) => {
        if (!stats) return false;

        switch (badge.id) {
            case 'first_contribution':
                return stats.totalContributions >= 1;
            case 'ten_contributions':
                return stats.totalContributions >= 10;
            case 'fifty_contributions':
                return stats.totalContributions >= 50;
            case 'hundred_contributions':
                return stats.totalContributions >= 100;
            case 'five_hundred_contributions':
                return stats.totalContributions >= 500;
            
            case 'first_language':
                return stats.languagesContributed.size >= 1;
            case 'multilingual':
                return stats.languagesContributed.size >= 3;
            case 'polyglot':
                return stats.languagesContributed.size >= 5;
            case 'linguistic_master':
                return stats.languagesContributed.size >= 10;
            
            case 'urban_explorer':
                return Array.from(stats.placeTypesContributed).some(type => 
                    type.includes('city') || type.includes('town') || type.includes('village'));
            case 'nature_lover':
                return Array.from(stats.placeTypesContributed).some(type => 
                    type.includes('park') || type.includes('forest') || type.includes('reserve'));
            case 'mountain_climber':
                return Array.from(stats.placeTypesContributed).some(type => 
                    type.includes('mountain') || type.includes('peak') || type.includes('hill'));
            case 'water_navigator':
                return Array.from(stats.placeTypesContributed).some(type => 
                    type.includes('river') || type.includes('lake') || type.includes('sea'));
            case 'place_type_master':
                return stats.placeTypesContributed.size >= 5;
            
            case 'daily_contributor':
                return stats.streakDays >= 7;
            case 'weekly_warrior':
                return stats.streakDays >= 30;
            
            case 'helpful_validator':
                return stats.validationContributions >= 5;
            
            default:
                return false;
        }
    };

    const getProgressForBadge = (badge, stats) => {
        if (!stats) return 0;

        switch (badge.id) {
            case 'first_contribution':
                return Math.min(stats.totalContributions / 1, 1);
            case 'ten_contributions':
                return Math.min(stats.totalContributions / 10, 1);
            case 'fifty_contributions':
                return Math.min(stats.totalContributions / 50, 1);
            case 'hundred_contributions':
                return Math.min(stats.totalContributions / 100, 1);
            case 'five_hundred_contributions':
                return Math.min(stats.totalContributions / 500, 1);
            
            case 'multilingual':
                return Math.min(stats.languagesContributed.size / 3, 1);
            case 'polyglot':
                return Math.min(stats.languagesContributed.size / 5, 1);
            case 'linguistic_master':
                return Math.min(stats.languagesContributed.size / 10, 1);
            
            case 'daily_contributor':
                return Math.min(stats.streakDays / 7, 1);
            case 'weekly_warrior':
                return Math.min(stats.streakDays / 30, 1);
            
            default:
                return 0;
        }
    };

    const filterBadges = (filterType) => {
        switch (filterType) {
            case 0: // All badges
                return badges;
            case 1: // Unlocked badges
                return badges.filter(badge => badge.isUnlocked());
            case 2: // Locked badges
                return badges.filter(badge => !badge.isUnlocked());
            default:
                return badges;
        }
    };

    const filteredBadges = filterBadges(tabValue);
    const unlockedCount = badges.filter(badge => badge.isUnlocked()).length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    minHeight: '70vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                color: 'white',
                m: 0
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrophyIcon />
                    <Typography variant="h6" component="div">
                        Achievement Badges ({unlockedCount}/{badges.length})
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {newBadges.length > 0 && (
                    <Alert severity="success" sx={{ mb: 3 }} icon={<StarIcon />}>
                        <Typography variant="body2">
                            🎉 Congratulations! You've unlocked {newBadges.length} new badge{newBadges.length > 1 ? 's' : ''}!
                        </Typography>
                    </Alert>
                )}

                {/* Tab Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab 
                            label={`All Badges (${badges.length})`} 
                            icon={<TrophyIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label={`Unlocked (${unlockedCount})`} 
                            icon={<StarIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label={`Locked (${badges.length - unlockedCount})`} 
                            icon={<LockIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Badge Grid */}
                <Grid container spacing={2}>
                    {filteredBadges.map((badge) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={badge.id}>
                            <BadgeCard
                                badge={badge}
                                isNew={newBadges.some(newBadge => newBadge.id === badge.id)}
                                showProgress={!badge.isUnlocked()}
                                progress={getProgressForBadge(badge, statistics)}
                            />
                        </Grid>
                    ))}
                </Grid>

                {filteredBadges.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {tabValue === 1 ? 'No badges unlocked yet' : 'No badges to show'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {tabValue === 1 
                                ? 'Start contributing to unlock your first achievements!'
                                : 'Start your linguistic research journey!'}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                {unlockedCount > 0 && (
                    <Button 
                        onClick={() => {
                            setShareType('achievement');
                            setShareDialogOpen(true);
                        }}
                        variant="outlined" 
                        startIcon={<ShareIcon />}
                        size="large"
                        sx={{ mr: 'auto' }}
                    >
                        Share Achievements
                    </Button>
                )}
                <Button onClick={onClose} variant="contained" size="large">
                    Close
                </Button>
            </DialogActions>

            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                shareType={shareType}
                statistics={statistics}
                badges={badges}
                achievements={newBadges}
            />
        </Dialog>
    );
};

export default BadgeSystem;